import { get } from "@/services/axiosUitils";
import { throwError } from "@/lib/throwError";

class PtsAttendanceService {
  /**
   * Generate attendance for an employee on a specific date
   * Calls: /pts_app/generate_attendance/
   * @param filterDate - Date in YYYY-MM-DD format
   * @param empId - Employee ID (optional)
   * @returns Generated attendance data
   */
  generateAttendance = async (
    filterDate: string,
    empId?: string
  ): Promise<any> => {
    if (!filterDate) {
      throwError("Filter date is required", "PtsAttendanceService");
    }

    const url = `/pts_app/generate_attendance/`;
    const params: any = {
      filter_date: filterDate,
    };

    // Only add emp_id if provided
    if (empId && empId.trim() !== "") {
      params.emp_id = empId;
    }

    return await get(url, { params });
  };

  /**
   * Get monthly attendance differences for employees
   * Calls: /pts_app/employee-dashboard/monthly-diffrents/
   * @param month - Month name (e.g., "January", "February")
   * @param year - Year (e.g., 2026)
   * @param empId - Employee ID (optional, if not provided or "all", fetches for all employees)
   * @returns Monthly attendance differences data
   */
  getMonthlyDifferences = async (
    month: string,
    year: number,
    empId?: string
  ): Promise<any> => {
    if (!month) {
      throwError("Month is required", "PtsAttendanceService");
    }
    if (!year) {
      throwError("Year is required", "PtsAttendanceService");
    }

    const url = `/pts_app/employee-dashboard/monthly-diffrents/`;
    const params: any = {
      filters: JSON.stringify({ month, year }),
    };

    // Only add emp_id if a specific employee is selected (not "all")
    if (empId && empId !== "all") {
      params.emp_id = empId;
    }

    return await get(url, {
      params,
    });
  };

  /**
   * Get monthly attendance sheet (employee rows × day columns with P/A/WO/H/etc.)
   * Calls: /pts_app/monthly-data/attendance-sheet/
   * @param month - Month name (e.g., "March")
   * @param year - Year (e.g., 2026)
   * @param empId - Employee ID (optional, if not provided or "all", fetches for all employees)
   * @returns { data: { employees: [{ empname, dates: { "1 Sun": "WO", ... } }] } }
   */
  getMonthlyAttendanceSheet = async (
    month: string,
    year: number,
    empId?: string
  ): Promise<{
    data?: {
      employees?: Array<{
        empname: string;
        emp_id?: string;
        shift?: string;
        dates: Record<string, string>;
      }>;
    };
  }> => {
    if (!month) {
      throwError("Month is required", "PtsAttendanceService");
    }
    if (!year) {
      throwError("Year is required", "PtsAttendanceService");
    }

    const url = `/pts_app/monthly-data/attendance-sheet/`;
    const params: Record<string, string> = {
      filters: JSON.stringify({ month, year }),
    };

    if (empId && empId !== "all") {
      params.emp_id = empId;
    }

    return await get(url, { params });
  };

  /**
   * Get yearly attendance summary for employee(s)
   * Calls: /pts_app/employee-dashboard/yearly-attendance/
   * @param year - Year (e.g., 2026)
   * @param empId - Employee ID (optional, if not provided or "all", fetches aggregate/all)
   */
  getYearlyAttendance = async (year: number, empId?: string): Promise<any> => {
    if (!year) {
      throwError("Year is required", "PtsAttendanceService");
    }

    const url = `/pts_app/employee-dashboard/yearly-attendance/`;
    const params: Record<string, string> = {
      filters: JSON.stringify({ year }),
    };

    if (empId && empId !== "all") {
      params.emp_id = empId;
    }

    return await get(url, { params });
  };
}

const instance = new PtsAttendanceService();
export default instance;
